import { Texture } from './Texture';
import { Context } from '../context/Context';
import { createColorTexture } from '../renderer/RenderTarget';
import { SceneRenderer } from '../renderer';
import { Scene } from '../scene/Scene';
import { Camera } from '../camera';

export class RenderedTexture extends Texture {
  private buffer!: WebGLFramebuffer;
  private ctx!: Context;
  private dummyTexture!: WebGLTexture;
  private depthRenderbuffer!: WebGLRenderbuffer;

  constructor(
    private depthTexture?: boolean,
    private dimensions?: TextureDimensions,
  ) {
    super();
    this.translucent = false;
  }

  public init(ctx: Context): void {
    this.ctx = ctx;

    if (!this.dimensions) {
      this.dimensions = ctx.viewPort;
    }

    const gl = ctx.gl;
    const { width, height } = this.dimensions;

    if (this.depthTexture) {
      this.glTexture = createColorTexture(
        gl,
        width,
        height,
        gl.DEPTH_COMPONENT24,
        gl.DEPTH_COMPONENT,
        gl.UNSIGNED_INT,
        gl.NEAREST,
      );
      this.dummyTexture = createColorTexture(gl, width, height, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE);
      this.buffer = this.createDepthFramebuffer();
    } else {
      this.glTexture = createColorTexture(gl, width, height, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE);
      this.buffer = this.createColorFramebuffer();
    }
  }

  private createColorFramebuffer(): WebGLFramebuffer {
    const gl = this.ctx.gl;
    const { width, height } = this.dimensions!;

    // Create and bind the framebuffer
    const fb = gl.createFramebuffer();
    gl.bindFramebuffer(gl.FRAMEBUFFER, fb);

    gl.framebufferTexture2D(
      gl.FRAMEBUFFER,
      gl.COLOR_ATTACHMENT0,
      gl.TEXTURE_2D,
      this.glTexture!,
      0,
    );

    // create a depth renderbuffer
    this.depthRenderbuffer = gl.createRenderbuffer();
    gl.bindRenderbuffer(gl.RENDERBUFFER, this.depthRenderbuffer);

    gl.renderbufferStorage(gl.RENDERBUFFER, gl.DEPTH_COMPONENT16, width, height);
    gl.framebufferRenderbuffer(
      gl.FRAMEBUFFER,
      gl.DEPTH_ATTACHMENT,
      gl.RENDERBUFFER,
      this.depthRenderbuffer,
    );

    if (gl.checkFramebufferStatus(gl.FRAMEBUFFER) !== gl.FRAMEBUFFER_COMPLETE) {
      throw new Error('this combination of attachments does not work');
    }

    return fb;
  }

  private createDepthFramebuffer(): WebGLFramebuffer {
    const gl = this.ctx.gl;

    // Depth framebuffers need a dummy color attachment
    const fb = gl.createFramebuffer();
    gl.bindFramebuffer(gl.FRAMEBUFFER, fb);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.DEPTH_ATTACHMENT, gl.TEXTURE_2D, this.glTexture!, 0);
    gl.framebufferTexture2D(
      gl.FRAMEBUFFER,
      gl.COLOR_ATTACHMENT0,
      gl.TEXTURE_2D,
      this.dummyTexture,
      0,
    );

    if (gl.checkFramebufferStatus(gl.FRAMEBUFFER) !== gl.FRAMEBUFFER_COMPLETE) {
      throw new Error('this combination of attachments does not work');
    }

    return fb;
  }

  public renderTexture(renderer: SceneRenderer, scene: Scene, camera: Camera): void {
    renderer.render(scene, camera, this.buffer);
  }

  public get framebuffer(): WebGLFramebuffer {
    return this.buffer;
  }

  public destroy(): void {
    const gl = this.ctx.gl;
    if (this.buffer) gl.deleteFramebuffer(this.buffer);
    if (this.depthRenderbuffer) gl.deleteRenderbuffer(this.depthRenderbuffer);
    if (this.glTexture) gl.deleteTexture(this.glTexture);
    if (this.dummyTexture) gl.deleteTexture(this.dummyTexture);
  }
}

type TextureDimensions = {
  width: number;
  height: number;
};
